import React, { useState } from 'react';
import {
  IAuditoriaRastreabilidadeBancariaResultado,
  IItemRastreabilidadeBancaria,
  IContaConciliadaRastreabilidadeBancaria,
  IDivergenciaReferencialRastreabilidadeBancaria,
  ILancamentoDetalheRastreabilidadeBancaria,
  ILancamentoConciliadoRastreabilidadeBancaria,
  IParametrosAplicadosRastreabilidadeBancaria,
} from '../interfaces/IAuditoriaRastreabilidadeBancaria';
import { AuditoriaRastreabilidadeBancariaService, ModoValidacaoRastreabilidade } from '../services/AuditoriaRastreabilidadeBancariaService';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaRastreabilidadeBancariaResultado;
  empresaId?: number;
  periodo?: string;
}

// COR-235: rótulos renomeados de "Razão Social" — o critério verifica participante identificável
// no histórico (nome, razão social ou qualquer outra identificação textual), não só pessoa jurídica.
const MODOS_VALIDACAO: { value: ModoValidacaoRastreabilidade; label: string; descricao: string }[] = [
  { value: 'RsEDoc',    label: 'Participante e Documento',    descricao: 'Obrigatório (padrão IN RFB nº 2.003/2021)' },
  { value: 'RsOuDoc',   label: 'Participante ou Documento',   descricao: 'Pelo menos um identificador' },
  { value: 'SomenteRs', label: 'Apenas Participante Identificado', descricao: 'Documento dispensado' },
  { value: 'SomenteDoc', label: 'Apenas Documento',  descricao: 'Participante Identificado dispensado' },
];

const LIMITE_INICIAL_DETALHE = 50;

const OPCOES_MOTIVO = [
  { label: 'Todos', valor: '' },
  { label: 'Sem Participante Identificado', valor: 'Participante não identificado' },
  { label: 'Sem Documento', valor: 'Número de Documento não localizado' },
  { label: 'Sem Ambos', valor: 'Participante Identificado e Número de Documento' },
];

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO': return 'bi-exclamation-circle-fill';
    case 'ALERTA': return 'bi-exclamation-triangle-fill';
    case 'AVISO': return 'bi-info-circle-fill';
    default: return 'bi-check-circle-fill';
  }
};

// COR-238: o CSV precisa refletir exatamente a parametrização ativa no momento da exportação —
// como a exportação é sempre client-side a partir do `resultadoAtual` já carregado na tela (mesmo
// padrão de todo módulo de auditoria), o conteúdo já é automaticamente coerente com o critério
// aplicado; o que faltava era TORNAR ISSO EXPLÍCITO no arquivo, para servir como evidência
// autocontida (sem depender do usuário lembrar/anexar um print da parametrização em separado).
const exportarCSV = (erros: IItemRastreabilidadeBancaria[], parametros: IParametrosAplicadosRastreabilidadeBancaria) => {
  const metaLinhas = [
    ['Parametrização ativa na exportação', parametros.descricaoModo],
    ['Critério de identificação aplicado', parametros.modoValidacao],
    ['Participante exigido', parametros.participanteExigido ? 'Sim' : 'Não (dispensado nesta configuração)'],
    ['Documento exigido', parametros.documentoExigido ? 'Sim' : 'Não (dispensado nesta configuração)'],
    ['Configuração personalizada', parametros.configuracaoPersonalizada ? 'Sim' : 'Não (padrão normativo)'],
    ['Justificativa da customização', parametros.justificativa || '—'],
    ['Contas excluídas do escopo', parametros.contasExcluidas.join(', ') || '—'],
    [],
  ];

  const cabecalho = [
    'Conta Bancária', 'Data', 'Valor (R$)', 'Histórico', 'Conta Contrapartida', 'Nº Lançamento',
    'Pendência', 'Tipo de Participante', 'Campos Verificados',
  ];
  const linhas = erros.flatMap(item =>
    item.lancamentosDetalhados.map(d => [
      item.descricaoConta,
      formatDate(d.data),
      d.valor.toFixed(2).replace('.', ','),
      d.historico,
      d.contaContraPartida,
      d.numeroLancamento,
      d.motivoPendencia,
      d.tipoParticipante === 'PESSOA_JURIDICA' ? 'Pessoa Jurídica' : 'Não determinado',
      item.camposVerificados,
    ])
  );
  const csv = [...metaLinhas, cabecalho, ...linhas]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'lancamentos_sem_identificacao.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AuditoriaRastreabilidadeBancariaDetalhe: React.FC<Props> = ({ resultado, empresaId, periodo }) => {
  const [resultadoAtual, setResultadoAtual] = useState(resultado);
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  // Painel de parametrização
  const [painelAberto, setPainelAberto] = useState(false);
  const [modoSelecionado, setModoSelecionado] = useState<ModoValidacaoRastreabilidade>('RsEDoc');
  const [contasInput, setContasInput] = useState('');
  // COR-238: justificativa obrigatória quando o modo reduz a exigência documental padrão —
  // validada também no backend (BadRequest se ausente), aqui só para dar feedback imediato.
  const [justificativaInput, setJustificativaInput] = useState('');
  const [aplicando, setAplicando] = useState(false);
  const [erroParametros, setErroParametros] = useState<string | null>(null);

  // Filtros globais (afetam a tabela principal de erros)
  const [filtroConta, setFiltroConta] = useState('');
  const [filtroMotivoGlobal, setFiltroMotivoGlobal] = useState('');

  // Controle por item expandido: filtro de motivo e paginação
  const [filtroMotivoDetalhe, setFiltroMotivoDetalhe] = useState<Record<string, string>>({});
  const [mostrarTodosDetalhe, setMostrarTodosDetalhe] = useState<Record<string, boolean>>({});

  const errosRastreabilidade = resultadoAtual.errosRastreabilidade || [];
  const contasConciliadas = resultadoAtual.contasConciliadas || [];
  const divergenciasReferencial = resultadoAtual.divergenciasReferencial || [];

  // COR-238: qualquer modo diferente do padrão normativo reduz a exigência documental — exige
  // justificativa (a parametrização personalizada precisa ser controlada e auditável).
  const precisaJustificativa = modoSelecionado !== 'RsEDoc';
  const justificativaFaltando = precisaJustificativa && !justificativaInput.trim();

  const aplicarParametros = async () => {
    if (!empresaId || !periodo) return;
    if (justificativaFaltando) {
      setErroParametros('Informe a justificativa para aplicar um critério menos restritivo que o padrão.');
      return;
    }
    setAplicando(true);
    setErroParametros(null);
    try {
      const contas = contasInput
        .split(',')
        .map(c => c.trim())
        .filter(Boolean);
      const novo = await AuditoriaRastreabilidadeBancariaService.buscarResultado({
        empresaId,
        periodo,
        modoValidacao: modoSelecionado,
        contasExcluidas: contas,
        justificativa: precisaJustificativa ? justificativaInput.trim() : undefined,
      });
      setResultadoAtual(novo);
      setItemExpandido(null);
    } catch (erro: any) {
      const mensagemBackend = erro?.response?.data?.mensagem;
      setErroParametros(mensagemBackend || 'Erro ao aplicar parametrização. Tente novamente.');
    } finally {
      setAplicando(false);
    }
  };

  const redefinirParametros = async () => {
    if (!empresaId || !periodo) return;
    setModoSelecionado('RsEDoc');
    setContasInput('');
    setJustificativaInput('');
    setAplicando(true);
    setErroParametros(null);
    try {
      const novo = await AuditoriaRastreabilidadeBancariaService.buscarResultado({ empresaId, periodo });
      setResultadoAtual(novo);
      setItemExpandido(null);
    } catch {
      setErroParametros('Erro ao redefinir configuração.');
    } finally {
      setAplicando(false);
    }
  };

  const errosFiltrados = errosRastreabilidade.filter(item => {
    if (filtroConta && !item.descricaoConta.toUpperCase().includes(filtroConta.toUpperCase())) return false;
    if (filtroMotivoGlobal && !item.lancamentosDetalhados.some(d => d.motivoPendencia.includes(filtroMotivoGlobal))) return false;
    return true;
  });

  const totalLancamentosFiltrados = errosFiltrados.reduce((acc, item) => acc + item.quantidadeOcorrencias, 0);
  const totalMultaFiltrada = errosFiltrados.reduce((acc, item) => acc + item.multa, 0);

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  const getDetalheFiltrado = (item: IItemRastreabilidadeBancaria, chave: string): ILancamentoDetalheRastreabilidadeBancaria[] => {
    const motivo = filtroMotivoDetalhe[chave] || '';
    if (!motivo) return item.lancamentosDetalhados;
    return item.lancamentosDetalhados.filter(d => d.motivoPendencia.includes(motivo));
  };

  const hayFiltrosAtivos = filtroConta || filtroMotivoGlobal;
  const parametrosAplicados = resultadoAtual.parametrosAplicados;
  const configuracaoPersonalizada = parametrosAplicados?.configuracaoPersonalizada;

  return (
    <>
      <div className={`auditoria-alert status-${resultadoAtual.status}`}>
        <i className={`bi ${getIconeStatus(resultadoAtual.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Rastreabilidade Bancária — {resultadoAtual.status}</strong>
          <div>{resultadoAtual.mensagemDirecionamento}</div>
        </div>
      </div>

      {/* Banner de configuração personalizada */}
      {configuracaoPersonalizada && parametrosAplicados && (
        <div
          className='d-flex align-items-center gap-2 flex-wrap p-3 mb-2'
          style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, fontSize: 13 }}
        >
          <i className='bi bi-sliders text-warning' />
          <strong>Configuração personalizada ativa:</strong>
          <span>{parametrosAplicados.descricaoModo}</span>
          {parametrosAplicados.contasExcluidas.length > 0 && (
            <span>· Contas excluídas: <code>{parametrosAplicados.contasExcluidas.join(', ')}</code></span>
          )}
          {parametrosAplicados.justificativa && (
            <span>· Justificativa: <em>{parametrosAplicados.justificativa}</em></span>
          )}
          {empresaId && periodo && (
            <button
              type='button'
              className='btn btn-sm btn-outline-secondary ms-auto'
              style={{ fontSize: 11 }}
              onClick={redefinirParametros}
              disabled={aplicando}
            >
              <i className='bi bi-arrow-counterclockwise me-1' />
              Redefinir para padrão normativo
            </button>
          )}
        </div>
      )}

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultadoAtual.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Multa Estimada (SPED)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultadoAtual.impactoFiscalTotal)}</div>
          {resultadoAtual.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% sobre lançamentos sem identificação, limitada a 1% da receita bruta do período (Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        <div className={`auditoria-kpi ${resultadoAtual.totalErros > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Risco Fiscal</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultadoAtual.totalErros}</span>
            <span className='ms-1' style={{ fontSize: 13, fontWeight: 400 }}>conta(s)</span>
          </div>
          {resultadoAtual.totalErros === 0 ? (
            <span className='auditoria-kpi-selo'>Sem Multa</span>
          ) : (
            <div className='auditoria-kpi-help'>Lançamentos sem Participante Identificado e/ou Número de Documento — passível de multa ECD potencial (5% limitada a 1% da receita bruta do período — Art. 12, II, Lei nº 8.218/1991).</div>
          )}
        </div>

        <div className={`auditoria-kpi ${resultadoAtual.totalAvisos > 0 ? '' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Qualidade Cadastral</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-aviso'>{resultadoAtual.totalAvisos}</span>
            <span className='ms-1' style={{ fontSize: 13, fontWeight: 400 }}>conta(s)</span>
          </div>
          <div className='auditoria-kpi-help'>Referenciais divergentes — achado de cadastro sem cálculo de multa. Corrija o referencial para incluir a conta no escopo de auditoria.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base contábil e fiscal aplicada à Rastreabilidade Bancária.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Contábil:</strong> {resultadoAtual.fundamentacaoContabil}</div>
          <div className='mt-2'><strong>Fiscal:</strong> {resultadoAtual.fundamentacaoFiscal}</div>
        </div>
      )}

      {/* Painel de parametrização — visível apenas quando empresaId e periodo estão disponíveis */}
      {empresaId && periodo && (
        <div className='auditoria-card' style={{ borderColor: painelAberto ? '#6366f1' : undefined }}>
          <div
            className='d-flex align-items-center justify-content-between'
            style={{ cursor: 'pointer' }}
            onClick={() => setPainelAberto(!painelAberto)}
          >
            <h3 className='mb-0' style={{ fontSize: 15 }}>
              <i className='bi bi-sliders me-2 text-primary' />
              Parametrização da Auditoria
              {configuracaoPersonalizada && (
                <span
                  className='ms-2'
                  style={{ background: '#fef9c3', color: '#92400e', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4 }}
                >
                  Personalizada
                </span>
              )}
            </h3>
            <i className={`bi ${painelAberto ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
          </div>

          {painelAberto && (
            <div className='mt-3'>
              <p className='text-muted' style={{ fontSize: 13 }}>
                Adapte o critério de rastreabilidade ao contexto operacional da empresa.
                O modo padrão (<strong>Participante e Documento</strong>) segue a exigência integral da IN RFB nº 2.003/2021.
                Modos alternativos reduzem a exigência — a escolha fica registrada no resultado para auditabilidade.
              </p>

              <div className='d-flex flex-wrap gap-3 mb-3'>
                {MODOS_VALIDACAO.map(m => (
                  <div
                    key={m.value}
                    className={`p-3 d-flex flex-column gap-1`}
                    style={{
                      flex: '1 1 180px',
                      borderRadius: 8,
                      border: `2px solid ${modoSelecionado === m.value ? '#6366f1' : '#e2e8f0'}`,
                      background: modoSelecionado === m.value ? '#eef2ff' : '#f8fafc',
                      cursor: 'pointer',
                    }}
                    onClick={() => setModoSelecionado(m.value)}
                  >
                    <div className='d-flex align-items-center gap-2'>
                      <input
                        type='radio'
                        name='modoValidacao'
                        checked={modoSelecionado === m.value}
                        onChange={() => setModoSelecionado(m.value)}
                        style={{ accentColor: '#6366f1' }}
                      />
                      <strong style={{ fontSize: 13 }}>{m.label}</strong>
                      {m.value === 'RsEDoc' && (
                        <span style={{ fontSize: 10, background: '#e0e7ff', color: '#3730a3', padding: '1px 5px', borderRadius: 4 }}>Padrão</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', paddingLeft: 22 }}>{m.descricao}</div>
                  </div>
                ))}
              </div>

              <div className='mb-3' style={{ maxWidth: 500 }}>
                <label className='form-label mb-1' style={{ fontSize: 13, fontWeight: 600 }}>
                  Contas excluídas do escopo
                  <span className='ms-1 text-muted fw-normal'>(opcional — separadas por vírgula)</span>
                </label>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='Ex: 1.01.01.02.001, 1.01.01.02.005'
                  value={contasInput}
                  onChange={e => setContasInput(e.target.value)}
                />
                <div className='form-text'>
                  Contas bancárias com padrão proprietário (caução, escrow, salário etc.) que não devem entrar no escopo desta auditoria.
                </div>
              </div>

              {precisaJustificativa && (
                <div className='mb-3' style={{ maxWidth: 500 }}>
                  <label className='form-label mb-1' style={{ fontSize: 13, fontWeight: 600 }}>
                    Justificativa da customização
                    <span className='ms-1' style={{ color: '#b91c1c' }}>(obrigatória)</span>
                  </label>
                  <textarea
                    className='form-control form-control-sm'
                    rows={2}
                    placeholder='Explique por que este critério menos restritivo que o padrão está sendo aplicado...'
                    value={justificativaInput}
                    onChange={e => setJustificativaInput(e.target.value)}
                  />
                  <div className='form-text'>
                    O modo selecionado reduz a exigência documental padrão (Participante Identificado e Número de
                    Documento). Esta justificativa fica registrada na trilha de auditoria.
                  </div>
                </div>
              )}

              {erroParametros && (
                <div className='alert alert-danger py-2 px-3 mb-3' style={{ fontSize: 13 }}>
                  <i className='bi bi-exclamation-triangle me-1' />
                  {erroParametros}
                </div>
              )}

              <div className='d-flex gap-2'>
                <button
                  type='button'
                  className='btn btn-primary btn-sm'
                  onClick={aplicarParametros}
                  disabled={aplicando || justificativaFaltando}
                  title={justificativaFaltando ? 'Informe a justificativa da customização para continuar' : undefined}
                >
                  {aplicando ? (
                    <><span className='spinner-border spinner-border-sm me-1' />Aplicando...</>
                  ) : (
                    <><i className='bi bi-check-lg me-1' />Aplicar parametrização</>
                  )}
                </button>
                {configuracaoPersonalizada && (
                  <button
                    type='button'
                    className='btn btn-outline-secondary btn-sm'
                    onClick={redefinirParametros}
                    disabled={aplicando}
                  >
                    <i className='bi bi-arrow-counterclockwise me-1' />
                    Redefinir para padrão
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className='auditoria-card'>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3'>
          <h3 className='mb-0'>
            Contas Bancárias com Lançamentos Sem Identificação
            <span
              className='ms-2'
              style={{ background: '#fee2e2', color: '#991b1b', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, verticalAlign: 'middle' }}
            >
              Risco Fiscal · Multa ECD Potencial
            </span>
          </h3>
          {errosRastreabilidade.length > 0 && (
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => exportarCSV(errosRastreabilidade, parametrosAplicados)}
              title='Exportar todos os lançamentos sem identificação como CSV'
            >
              <i className='bi bi-download me-1' />
              Exportar CSV ({errosRastreabilidade.reduce((a, i) => a + i.quantidadeOcorrencias, 0)} lançamentos)
            </button>
          )}
        </div>

        <p className='text-muted'>
          <strong style={{ color: '#991b1b' }}>Risco fiscal:</strong> lançamentos em contas de movimento bancário
          cujo histórico não permite identificar o participante (nome, razão social ou pessoa física) e/ou o Número de Documento/Nota Fiscal.
          Consolidado por conta — uma linha por conta com o total de ocorrências no período.
          Operações estruturalmente identificadas por natureza (tributo, tarifa, aplicação/resgate, folha, financiamento) são excluídas.
          <strong> Base de cálculo da multa ECD potencial</strong> (5% limitada a 1% da receita bruta do período) por inexatidão no SPED (Art. 12, II, Lei nº 8.218/1991).
        </p>

        {/* Filtros globais */}
        {errosRastreabilidade.length > 0 && (
          <div className='d-flex flex-wrap gap-3 mb-3 p-3' style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <div style={{ flex: '1 1 220px' }}>
              <label className='form-label mb-1' style={{ fontSize: 12, fontWeight: 600 }}>Filtrar por conta</label>
              <div className='input-group input-group-sm'>
                <span className='input-group-text'><i className='bi bi-search' /></span>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Nome da conta...'
                  value={filtroConta}
                  onChange={e => setFiltroConta(e.target.value)}
                />
                {filtroConta && (
                  <button className='btn btn-outline-secondary' type='button' onClick={() => setFiltroConta('')}>
                    <i className='bi bi-x' />
                  </button>
                )}
              </div>
            </div>

            <div style={{ flex: '1 1 280px' }}>
              <label className='form-label mb-1' style={{ fontSize: 12, fontWeight: 600 }}>Filtrar por motivo de enquadramento</label>
              <div className='d-flex flex-wrap gap-1'>
                {OPCOES_MOTIVO.map(op => (
                  <button
                    key={op.valor}
                    type='button'
                    className={`btn btn-sm ${filtroMotivoGlobal === op.valor ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setFiltroMotivoGlobal(op.valor)}
                    style={{ fontSize: 12 }}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            </div>

            {hayFiltrosAtivos && (
              <div className='d-flex align-items-end'>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  <i className='bi bi-funnel-fill me-1' />
                  {errosFiltrados.length} conta(s) · {totalLancamentosFiltrados} ocorrência(s) · multa {formatCurrency(totalMultaFiltrada)}
                  <button
                    type='button'
                    className='btn btn-sm btn-link p-0 ms-2'
                    style={{ fontSize: 12 }}
                    onClick={() => { setFiltroConta(''); setFiltroMotivoGlobal(''); }}
                  >
                    Limpar filtros
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {errosFiltrados.length === 0 && errosRastreabilidade.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma conta bancária com lançamentos sem identificação no período.</div>
        )}

        {errosFiltrados.length === 0 && errosRastreabilidade.length > 0 && (
          <div className='text-muted mt-4'>Nenhuma conta corresponde aos filtros aplicados.</div>
        )}

        {errosFiltrados.length > 0 && (
          <div className='table-responsive mt-2'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th className='text-center'>Ocorrências</th>
                  <th>Período</th>
                  <th className='text-end'>Valor Total</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {errosFiltrados.map((item: IItemRastreabilidadeBancaria, idx) => {
                  const chave = `rb-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  const detalheFiltrado = getDetalheFiltrado(item, chave);
                  const mostrarTodos = mostrarTodosDetalhe[chave] || false;
                  const detalheVisivel = mostrarTodos ? detalheFiltrado : detalheFiltrado.slice(0, LIMITE_INICIAL_DETALHE);
                  const motivoAtivo = filtroMotivoDetalhe[chave] || '';

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td className='text-center'>{item.quantidadeOcorrencias}</td>
                        <td>{formatDate(item.dataPrimeiraOcorrencia)} a {formatDate(item.dataUltimaOcorrencia)}</td>
                        <td className='text-end'>{formatCurrency(item.valorTotal)}</td>
                        <td className='text-end'>{formatCurrency(item.multa)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: '#fafbfc', padding: 0 }}>
                            <div className='p-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div className='mt-1'><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              {item.camposVerificados && (
                                <div className='mt-1 text-muted' style={{ fontSize: 12 }}>
                                  <i className='bi bi-search me-1' />
                                  <strong>Campos verificados:</strong> {item.camposVerificados}
                                </div>
                              )}

                              {/* Detalhe dos lançamentos */}
                              {item.lancamentosDetalhados && item.lancamentosDetalhados.length > 0 && (
                                <div className='mt-3'>
                                  <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2'>
                                    <strong style={{ fontSize: 13 }}>
                                      Lançamentos sem identificação — {item.quantidadeOcorrencias} ocorrência(s)
                                    </strong>
                                    <div className='d-flex align-items-center gap-2 flex-wrap'>
                                      {/* Filtro local por motivo */}
                                      <div className='d-flex gap-1' style={{ fontSize: 11 }}>
                                        {OPCOES_MOTIVO.map(op => (
                                          <button
                                            key={op.valor}
                                            type='button'
                                            className={`btn btn-xs ${motivoAtivo === op.valor ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            style={{ fontSize: 11, padding: '2px 8px' }}
                                            onClick={e => { e.stopPropagation(); setFiltroMotivoDetalhe(m => ({ ...m, [chave]: op.valor })); }}
                                          >
                                            {op.label}
                                          </button>
                                        ))}
                                      </div>
                                      {/* Exportar conta individual */}
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-outline-primary'
                                        style={{ fontSize: 11 }}
                                        onClick={e => { e.stopPropagation(); exportarCSV([item], parametrosAplicados); }}
                                        title='Exportar lançamentos desta conta como CSV'
                                      >
                                        <i className='bi bi-download me-1' />
                                        CSV
                                      </button>
                                    </div>
                                  </div>

                                  {detalheFiltrado.length === 0 ? (
                                    <div className='text-muted' style={{ fontSize: 12 }}>Nenhum lançamento corresponde ao filtro.</div>
                                  ) : (
                                    <>
                                      <div className='table-responsive'>
                                        <table className='auditoria-table' style={{ fontSize: 12 }}>
                                          <thead>
                                            <tr>
                                              <th style={{ whiteSpace: 'nowrap' }}>Data</th>
                                              <th className='text-end' style={{ whiteSpace: 'nowrap' }}>Valor</th>
                                              <th>Histórico</th>
                                              <th style={{ whiteSpace: 'nowrap' }}>Contrapartida</th>
                                              <th style={{ whiteSpace: 'nowrap' }}>Nº Lançamento</th>
                                              <th>Pendência</th>
                                              <th style={{ whiteSpace: 'nowrap' }}>Tipo de Participante</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {detalheVisivel.map((d: ILancamentoDetalheRastreabilidadeBancaria, di: number) => (
                                              <tr key={di}>
                                                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(d.data)}</td>
                                                <td className='text-end' style={{ whiteSpace: 'nowrap' }}>{formatCurrency(d.valor)}</td>
                                                <td
                                                  style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                  title={d.historico}
                                                >
                                                  {d.historico || '—'}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{d.contaContraPartida || '—'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{d.numeroLancamento || '—'}</td>
                                                <td>
                                                  <span
                                                    className='badge'
                                                    style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 500, fontSize: 11, whiteSpace: 'normal' }}
                                                  >
                                                    {d.motivoPendencia}
                                                  </span>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', fontSize: 11, color: '#6b7280' }}>
                                                  {d.tipoParticipante === 'PESSOA_JURIDICA' ? 'Pessoa Jurídica' : 'Não determinado'}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Paginação */}
                                      {detalheFiltrado.length > LIMITE_INICIAL_DETALHE && (
                                        <div className='mt-2 text-center'>
                                          {!mostrarTodos ? (
                                            <button
                                              type='button'
                                              className='btn btn-sm btn-outline-secondary'
                                              style={{ fontSize: 12 }}
                                              onClick={e => { e.stopPropagation(); setMostrarTodosDetalhe(m => ({ ...m, [chave]: true })); }}
                                            >
                                              <i className='bi bi-chevron-down me-1' />
                                              Mostrar todos os {detalheFiltrado.length} lançamentos
                                            </button>
                                          ) : (
                                            <button
                                              type='button'
                                              className='btn btn-sm btn-outline-secondary'
                                              style={{ fontSize: 12 }}
                                              onClick={e => { e.stopPropagation(); setMostrarTodosDetalhe(m => ({ ...m, [chave]: false })); }}
                                            >
                                              <i className='bi bi-chevron-up me-1' />
                                              Recolher (mostrar {LIMITE_INICIAL_DETALHE} mais recentes)
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {contasConciliadas.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Contas Bancárias Verificadas e Conciliadas
          </h3>
          <p className='text-muted'>
            Contas cujos lançamentos avaliados no período têm Participante Identificado e Número de Documento identificáveis nos
            campos da ECD verificados (ou são estruturalmente identificados por natureza).
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th className='text-center'>Lançamentos Verificados</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasConciliadas.map((item: IContaConciliadaRastreabilidadeBancaria, idx) => {
                  const chave = `cc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td className='text-center'>{item.quantidadeLancamentosVerificados}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={3} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <div>
                                <i className='bi bi-check-circle-fill text-success me-1' />
                                <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                              </div>

                              {/* Breakdown por critério de conciliação */}
                              <div className='mt-2 d-flex gap-2 flex-wrap' style={{ fontSize: 12 }}>
                                {(item.quantidadeIdentificados ?? 0) > 0 && (
                                  <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 4 }}>
                                    <i className='bi bi-person-check me-1' />
                                    {item.quantidadeIdentificados} por Participante Identificado + Documento
                                  </span>
                                )}
                                {(item.quantidadeEstruturais ?? 0) > 0 && (
                                  <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 4 }}>
                                    <i className='bi bi-shield-check me-1' />
                                    {item.quantidadeEstruturais} por natureza estrutural
                                  </span>
                                )}
                              </div>

                              {item.motivoConciliacao && (
                                <div className='mt-1' style={{ fontSize: 12, color: '#166534' }}>
                                  <strong>Critério:</strong> {item.motivoConciliacao}
                                </div>
                              )}

                              {/* Amostra de lançamentos com critério individual */}
                              {item.lancamentosAmostra && item.lancamentosAmostra.length > 0 && (
                                <div className='mt-2'>
                                  <strong style={{ fontSize: 12 }}>
                                    Amostra de lançamentos verificados
                                    {item.lancamentosAmostra.length < item.quantidadeLancamentosVerificados
                                      ? ` (${item.lancamentosAmostra.length} de ${item.quantidadeLancamentosVerificados})`
                                      : ` (${item.lancamentosAmostra.length})`}
                                    :
                                  </strong>
                                  <div className='table-responsive mt-1'>
                                    <table className='auditoria-table' style={{ fontSize: 11 }}>
                                      <thead>
                                        <tr>
                                          <th style={{ whiteSpace: 'nowrap' }}>Data</th>
                                          <th className='text-end' style={{ whiteSpace: 'nowrap' }}>Valor</th>
                                          <th>Histórico</th>
                                          <th>Critério de Conciliação</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.lancamentosAmostra.map((l: ILancamentoConciliadoRastreabilidadeBancaria, li: number) => {
                                          const isEstrututal = l.criterioConciliacao.startsWith('Operação estrutural');
                                          return (
                                            <tr key={li}>
                                              <td style={{ whiteSpace: 'nowrap' }}>{formatDate(l.data)}</td>
                                              <td className='text-end' style={{ whiteSpace: 'nowrap' }}>{formatCurrency(l.valor)}</td>
                                              <td
                                                style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                title={l.historico}
                                              >
                                                {l.historico || '—'}
                                              </td>
                                              <td>
                                                <span style={{
                                                  background: isEstrututal ? '#e0f2fe' : '#dcfce7',
                                                  color: isEstrututal ? '#0369a1' : '#166534',
                                                  padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 500
                                                }}>
                                                  {l.criterioConciliacao}
                                                </span>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                  {item.lancamentosAmostra.length < item.quantidadeLancamentosVerificados && (
                                    <div className='mt-1 text-muted' style={{ fontSize: 11 }}>
                                      <i className='bi bi-info-circle me-1' />
                                      Exibindo os {item.lancamentosAmostra.length} primeiros por data. Total verificado: {item.quantidadeLancamentosVerificados} lançamentos.
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {divergenciasReferencial.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-info-circle-fill text-warning me-2' style={{ fontSize: 18 }} />
            Contas com Referencial Possivelmente Divergente
            <span
              className='ms-2'
              style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, verticalAlign: 'middle' }}
            >
              Qualidade Cadastral · Sem Multa
            </span>
          </h3>
          <p className='text-muted'>
            <strong style={{ color: '#92400e' }}>Qualidade cadastral:</strong> contas cujo nome cita instituição financeira
            mas cujo referencial cadastrado não corresponde ao grupo de Bancos Conta Movimento (1.01.01.02).
            Indício de mapeamento incorreto no sistema contábil de origem — <strong>sem exposição fiscal direta e sem cálculo de multa.</strong>{' '}
            <strong>Alta confiança</strong> (bancos formais) tem sugestão automática de reclassificação;{' '}
            <strong>média confiança</strong> (meios de pagamento, adquirentes, carteiras digitais) só sinaliza para
            revisão manual, sem sugerir automaticamente o referencial de banco.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Referencial Cadastrado</th>
                  <th>Confiança</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {divergenciasReferencial.map((item: IDivergenciaReferencialRastreabilidadeBancaria, idx) => {
                  const chave = `dr-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  const altaConfianca = item.nivelConfianca === 'ALTA';

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>
                          {item.contaReferencial}
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.descricaoReferencialAtual}</div>
                        </td>
                        <td>
                          <span
                            style={{
                              background: altaConfianca ? '#fee2e2' : '#e0f2fe',
                              color: altaConfianca ? '#991b1b' : '#0369a1',
                              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                            }}
                          >
                            {altaConfianca ? 'Alta — Banco Formal' : 'Média — Revisão Manual'}
                          </span>
                        </td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              {item.justificativaTecnica && (
                                <div className='mt-1'><strong>Justificativa técnica:</strong> {item.justificativaTecnica}</div>
                              )}
                              {item.categoriaSugerida && (
                                <div className='mt-1'><strong>Categoria:</strong> {item.categoriaSugerida}</div>
                              )}

                              {altaConfianca && item.referencialSugerido && (
                                <div
                                  className='mt-2 p-2 d-flex align-items-center gap-2 flex-wrap'
                                  style={{ background: '#fff8e1', border: '1px solid #fdd835', borderRadius: 6 }}
                                >
                                  <i className='bi bi-arrow-right-circle-fill' style={{ color: '#f59e0b' }} />
                                  <strong>Referencial sugerido:</strong>
                                  <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                                    {item.referencialSugerido}
                                  </code>
                                  <span>—</span>
                                  <span>{item.descricaoReferencialSugerido}</span>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-outline-secondary'
                                    style={{ fontSize: 11, padding: '1px 8px' }}
                                    title='Copiar código do referencial sugerido'
                                    onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(item.referencialSugerido); }}
                                  >
                                    <i className='bi bi-clipboard me-1' />
                                    Copiar
                                  </button>
                                </div>
                              )}

                              {!altaConfianca && (
                                <div
                                  className='mt-2 p-2'
                                  style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6 }}
                                >
                                  <i className='bi bi-exclamation-circle me-1' style={{ color: '#0369a1' }} />
                                  Sem sugestão automática de referencial — confirme a natureza da conta manualmente
                                  antes de qualquer reclassificação.
                                </div>
                              )}

                              {item.acaoSugerida && (
                                <div className='mt-2'>
                                  <strong>Ação sugerida:</strong> {item.acaoSugerida}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaRastreabilidadeBancariaDetalhe;
